import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drop } from '@/types';
import { api } from '@/services/api';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import DropCard from '@/components/drops/DropCard';

const getUserDrops = async (): Promise<Drop[]> => {
  const response = await api.get('/api/drops/me');
  if (response && response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

const MyDropsPage: React.FC = () => {
  const { data: drops = [], isLoading, error } = useQuery<Drop[]>({
    queryKey: ['myDrops'],
    queryFn: getUserDrops,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-800 rounded-lg p-4 h-48"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-500">
          Error loading drops. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">My Drops</h1>
        <Link
          to="/dashboard/drops/create"
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>Create New Drop</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {drops.map((drop: Drop) => (
          <div key={drop.id} className="card">
            <DropCard drop={drop} />
            <div className="p-4 border-t border-dark-700">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  Created: {new Date(drop.createdAt).toLocaleDateString()}
                </span>
                <div className="flex space-x-2">
                  <Link
                    to={`/dashboard/drops/${drop.id}/edit`}
                    className="btn-icon btn-outline"
                    title="Edit Drop"
                  >
                    <FiEdit2 />
                  </Link>
                  <button
                    className="btn-icon btn-outline text-red-500 hover:text-red-600"
                    title="Delete Drop"
                    onClick={() => {
                      // TODO: Implement delete functionality
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {drops.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">You haven't created any drops yet</div>
            <Link to="/dashboard/drops/create" className="btn-primary">
              Create Your First Drop
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDropsPage;
